import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import {
  AuthApiService,
  RolesApiService,
  Role,
} from '@angular-workspace/services';
import { ToasterService } from '../services/toaster.service';
import { LoginModalService, isKeycloakAuthInProgress } from '../services/login-modal.service';
import {
  getMahaAgxPanelKeyFromPath,
  getRedirectRouteForHigherRoleUser,
  MAHA_AGX_PROTECTED_PANELS,
  type MahaPanel,
} from '../utils/maha-panel-access.util';
import { WorkspaceContextService } from '../services/workspace-context.service';

export { MAHA_AGX_PROTECTED_PANELS } from '../utils/maha-panel-access.util';

/**
 * Deny access to a post-login panel route and prompt the user to sign in.
 * The URL is left unchanged; only the login modal is shown.
 * @returns `false` — route activation must be blocked
 */
function denyUnauthenticatedPanelAccess(
  attemptedUrl: string,
  loginModal: LoginModalService
): false {
  if (!isKeycloakAuthInProgress()) {
    loginModal.open(attemptedUrl);
  }
  return false;
}

function isMahaAgxDefaultPanelLandingPath(url: string, panel: MahaPanel): boolean {
  const clean = url.split('?')[0].replace(/\/$/, '') || '/';
  switch (panel) {
    case 'consumer':
      return clean === '/consumer' || clean === '/consumer/home';
    case 'provider':
      return (
        clean === '/provider' || clean === '/provider/home' || clean === '/provider/dashboard'
      );
    case 'org-admin':
      return clean === '/org-admin' || clean === '/org-admin/home' || clean === '/org-admin/dashboard';
    case 'admin':
      return clean === '/admin' || clean === '/admin/home' || clean === '/admin/dashboard';
    default:
      return false;
  }
}

/**
 * @returns `true` if the route can activate, `false` if redirect navigation was started
 */
function tryRedirectToHighestPanelHome(
  stateUrl: string,
  rolesService: RolesApiService,
  router: Router
): boolean {
  const redirectRoute = getRedirectRouteForHigherRoleUser(stateUrl, rolesService);
  if (!redirectRoute) {
    return true;
  }

  const currentNorm = stateUrl.split('?')[0].replace(/\/$/, '') || '/';
  const targetNorm = redirectRoute.split('?')[0].replace(/\/$/, '') || '/';
  if (currentNorm === targetNorm || stateUrl.startsWith(redirectRoute)) {
    return true;
  }

  void router.navigateByUrl(redirectRoute, { replaceUrl: true });
  return false;
}

async function syncMahaAgxUserProfileForGuard(
  authApi: AuthApiService,
  rolesService: RolesApiService
): Promise<void> {
  const userData = await authApi.getUserInfo();
  localStorage.setItem('userData', JSON.stringify(userData));
  await rolesService.getRoles();
}

/**
 * Auth Guard for role-based access control
 * Protects routes based on user roles and login status
 */
export const authGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  // Inject all services at the top level (required for functional guards)
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const rolesService = inject(RolesApiService);
  const authApiService = inject(AuthApiService);
  const toaster = inject(ToasterService);
  const loginModal = inject(LoginModalService);
  const workspaceContext = inject(WorkspaceContextService);

  const currentPath = state.url;
  const isPanelRoute = MAHA_AGX_PROTECTED_PANELS.some((panel) =>
    currentPath.startsWith(panel)
  );

  // Debounce repeated "Access denied" toasters (guard can run multiple times per navigation)
  const ACCESS_DENIED_DEBOUNCE_MS = 3000;
  const lastAccessDeniedKey = 'auth-guard-last-access-denied';
  const lastAccessDenied = parseInt(sessionStorage.getItem(lastAccessDeniedKey) || '0', 10);

  // For non-panel routes (e.g. /contact-us, /contact-us/thank-you) we only need
  // to confirm the user is authenticated — no role check needed.
  // Use a direct Keycloak check here instead of the complex fallback logic below
  // (which is lenient about stale storage tokens and would let unauthenticated users through).
  if (!isPanelRoute) {
    try {
      const loggedIn = await keycloak.isLoggedIn();
      if (!loggedIn) {
        keycloak.login({ redirectUri: window.location.origin + state.url });
        return false;
      }
      return true;
    } catch {
      keycloak.login({ redirectUri: window.location.origin + state.url });
      return false;
    }
  }

  try {
    
    // Wait for Keycloak to be initialized (important for page refresh)
    // Try to get the instance - if it fails, wait a bit and try again
    let keycloakInstance;
    let attempts = 0;
    const maxAttempts = 10; // Increased attempts for page refresh
    
    while (attempts < maxAttempts) {
      try {
        keycloakInstance = keycloak.getKeycloakInstance();
        if (keycloakInstance) {
          break;
        }
      } catch (e) {
        // Instance not ready yet
      }
      
      if (attempts < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, 200)); // Wait 200ms
      }
      attempts++;
    }
    
    const kc = keycloakInstance as { token?: string; refreshToken?: string } | null;
    const hasStoredTokens = Boolean(kc && (kc.token || kc.refreshToken));
    const isKeycloakInitializing =
      sessionStorage.getItem('keycloak-initializing') === 'true';

    let isLoggedIn = false;

    try {
      const loginCheckPromise = keycloak.isLoggedIn();
      const timeoutPromise = new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), 5000);
      });
      isLoggedIn = await Promise.race([loginCheckPromise, timeoutPromise]);

      // One retry while Keycloak restores a session after page refresh.
      if (!isLoggedIn && hasStoredTokens) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          isLoggedIn = await keycloak.isLoggedIn();
        } catch {
          // Resolved below
        }
      }
    } catch (loginCheckError) {
      console.error('Error checking login status:', loginCheckError);
    }

    if (!isLoggedIn) {
      // Allow temporary access only while Keycloak is still booting AND tokens exist.
      if (hasStoredTokens && isKeycloakInitializing) {
        return true;
      }
      return denyUnauthenticatedPanelAccess(currentPath, loginModal);
    }

    loginModal.close();

    // User is logged in - check roles (with error handling)
    try {
      // Try to get roles with timeout
      const rolesPromise = rolesService.getRoles();
      const rolesTimeout = new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 3000); // 3 second timeout
      });
      
      await Promise.race([rolesPromise, rolesTimeout]);

      if (isLoggedIn) {
        const needProfileSync =
          !rolesService.getUserRoles().length ||
          (getMahaAgxPanelKeyFromPath(currentPath) === 'consumer' &&
            isMahaAgxDefaultPanelLandingPath(currentPath, 'consumer'));
        if (needProfileSync) {
          try {
            await syncMahaAgxUserProfileForGuard(authApiService, rolesService);
          } catch (e) {
            console.warn('auth guard: could not sync user from control plane', e);
          }
        }
      }

      const userRoles = rolesService.getUserRoles();

      // Determine which panel the user is trying to access
      let requiredRole: Role | null = null;
      
      if (currentPath.startsWith('/admin')) {
        requiredRole = Role.ADMIN;
      } else if (currentPath.startsWith('/org-admin')) {
        requiredRole = Role.ORG_ADMIN;
      } else if (currentPath.startsWith('/provider')) {
        requiredRole = Role.PROVIDER;
      } else if (currentPath.startsWith('/consumer')) {
        requiredRole = Role.CONSUMER;
      }

      try {
        await workspaceContext.loadDelegatedWorkspaces();
      } catch {
        // Keep going with whatever cache we already have.
      }

      const delegatedRole = workspaceContext.getActiveDelegatedPanelRole();
      if (workspaceContext.isDelegateSession()) {
        if (requiredRole && delegatedRole && delegatedRole !== requiredRole) {
          const delegatedWs = workspaceContext.getActiveDelegatedWorkspace();
          if (delegatedWs) {
            const target = workspaceContext.panelRouteFor(delegatedWs);
            const currentNorm = currentPath.split('?')[0].replace(/\/$/, '') || '/';
            const targetNorm = target.replace(/\/$/, '') || '/';
            if (currentNorm !== targetNorm) {
              void router.navigate([target]);
              return false;
            }
          }
        }
        return true;
      }

      if (!userRoles || userRoles.length === 0) {
        return true;
      }
      if (requiredRole && delegatedRole === requiredRole) {
        return true;
      }
      if (delegatedRole && requiredRole && delegatedRole !== requiredRole) {
        const delegatedWs = workspaceContext.getActiveDelegatedWorkspace();
        if (delegatedWs) {
          const target = workspaceContext.panelRouteFor(delegatedWs);
          const currentNorm = currentPath.split('?')[0].replace(/\/$/, '') || '/';
          const targetNorm = target.replace(/\/$/, '') || '/';
          if (currentNorm !== targetNorm) {
            void router.navigate([target]);
            return false;
          }
        }
      }

      // Check if user has the required role (use hasRole to handle aliases like cos_admin -> admin)
      if (requiredRole) {
        const hasRequiredRole = rolesService.hasRole(requiredRole);
        const panel = getMahaAgxPanelKeyFromPath(currentPath);
        const mustUseHigherPanel =
          panel != null && getRedirectRouteForHigherRoleUser(currentPath, rolesService) != null;

        if (!hasRequiredRole || mustUseHigherPanel) {
          // User doesn't have access to this panel
          if (Date.now() - lastAccessDenied > ACCESS_DENIED_DEBOUNCE_MS) {
            sessionStorage.setItem(lastAccessDeniedKey, String(Date.now()));
            toaster.warning(
              `Access denied. You don't have permission to access this panel.`,
              4000
            );
          }

          // Redirect to their highest role panel
          const userPanelRoute =
            getRedirectRouteForHigherRoleUser(currentPath, rolesService) ??
            rolesService.getUserPanelRoute();
          router.navigate([userPanelRoute]);
          return false;
        }
      }

      if (
        !workspaceContext.isDelegateSession() &&
        !tryRedirectToHighestPanelHome(currentPath, rolesService, router)
      ) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking roles:', error);
      return true;
    }
  } catch (error) {
    console.error('Auth guard critical error:', error);
    return denyUnauthenticatedPanelAccess(currentPath, loginModal);
  }
};

/**
 * Logout Guard - Handles logout redirects
 * Use this on routes that should trigger logout message
 */
export const logoutGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const keycloak = inject(KeycloakService);
  const router = inject(Router);
  const toaster = inject(ToasterService);

  try {
    const isLoggedIn = await keycloak.isLoggedIn();

    if (!isLoggedIn) {
      // Mark this as a logout redirect
      sessionStorage.setItem('logout-redirect', 'true');
      toaster.info("You've successfully logged out", 3000);
      router.navigate(['/']);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Logout guard error:', error);
    return true;
  }
};
